import { OcrPattern, OcrPoint, TextDetection } from './ocr.model';

const MPAN_LABEL = 'S';
const DIGITS_AFTER_LABEL = 4;
const MIN_READING_LENGTH = 12;

function matches(result: TextDetection, pattern: string): boolean {
  return (result.DetectedText ?? '').match(pattern) !== null;
}

function firstWordMatching(results: TextDetection[], pattern: string): TextDetection | undefined {
  return results.filter(result => result.Type === 'WORD').find(result => matches(result, pattern));
}

function lastLineMatching(results: TextDetection[], pattern: string): TextDetection | undefined {
  return results
    .filter(result => result.Type === 'LINE')
    .filter(result => matches(result, pattern))
    .pop();
}

function matchFor(results: TextDetection[], pattern: OcrPattern): TextDetection | undefined {
  if (pattern.type === 'standard') {
    return firstWordMatching(results, pattern.patterns[0]);
  }
  if (pattern.type === 'split') {
    return lastLineMatching(results, pattern.patterns[0]);
  }
  return undefined;
}

function leftEdge(result: TextDetection): number {
  return result.Geometry?.Polygon[0].X ?? 0;
}

function corner(result: TextDetection, index: number): OcrPoint {
  return { X: result.Geometry?.Polygon[index].X ?? 0, Y: result.Geometry?.Polygon[index].Y ?? 0 };
}

function readingAfterMpanLabel(results: TextDetection[]): TextDetection {
  const labelIndex = results.findIndex(result => result.DetectedText === MPAN_LABEL);
  if (labelIndex < 0) {
    return {};
  }

  const digits = results
    .slice(labelIndex + 1, labelIndex + 1 + DIGITS_AFTER_LABEL)
    .sort((a, b) => leftEdge(a) - leftEdge(b))
    .filter(result => /^[0-9 ]+$/.test(result.DetectedText ?? ''));

  let text = '';
  let lastIndex = DIGITS_AFTER_LABEL;
  for (let index = 0; index < digits.length; index++) {
    text += digits[index].DetectedText?.replace(/\s/g, '') ?? '';
    if (text.length > MIN_READING_LENGTH) {
      lastIndex = index;
      break;
    }
  }

  const first = digits[0];
  const last = digits[lastIndex];
  if (!first || !last) {
    return {};
  }
  return {
    DetectedText: text,
    Geometry: { Polygon: [corner(first, 0), corner(last, 1), corner(last, 2), corner(first, 3)] }
  };
}

export function pickOcrReading(results: TextDetection[], patterns: readonly OcrPattern[] = []): TextDetection {
  const patternMatch = patterns.reduce<TextDetection>((picked, pattern) => matchFor(results, pattern) ?? picked, {});
  return patternMatch.DetectedText ? patternMatch : readingAfterMpanLabel(results);
}
